import axios from 'axios'
import moment from 'moment'
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import * as Icons from 'react-feather'
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts'
import { getStorageValues } from '../../shared/CommonFunctions'
import { getApiBaseUrl } from '../../utils/apiConfig'
import { deleteSalesPaymentReceipt, deleteSalesPaymentFollowup, updateSalesPaymentFollowup, updateSalesPaymentReceipt } from '../../utils/salesRegisterApi'
import { Link } from 'react-router-dom'

const PaymentFollowupRowFollowup = forwardRef(({ data, onReceiptChanged }, ref) => {
  const [alert, setAlert] = useState(null)
  const [followupList, setFollowupList] = useState([])
  const [paymentHistory, setPaymentHistory] = useState([])
  const [followupDate, setFollowupDate] = useState(new Date())
  const [remarks, setRemarks] = useState('')
  const [editingReceiptId, setEditingReceiptId] = useState(null)
  const [editForm, setEditForm] = useState({ ReceiptDate: new Date(), Amount: '', Remarks: '' })
  const [savingReceiptId, setSavingReceiptId] = useState(null)
  const [editingFollowupId, setEditingFollowupId] = useState(null)
  const [editingFollowupDate, setEditingFollowupDate] = useState(null)
  const [editingFollowupText, setEditingFollowupText] = useState('')
  const [hoveredFollowupId, setHoveredFollowupId] = useState(null)
  const [followupSaving, setFollowupSaving] = useState(false)
  const [deleteFollowupConfirm, setDeleteFollowupConfirm] = useState({
    show: false,
    followupId: null,
    followupText: '',
    followupUser: '',
    followupDate: ''
  })

  const salesRegisterId = data?.SalesRegisterId
  const userId = useMemo(() => getStorageValues('userId'), [])
  const currentUserId = useMemo(() => Number(getStorageValues('userId')), [])

  useImperativeHandle(ref, () => ({
    refreshFollowup: fetchFollowups,
    refreshPaymentHistory: fetchPaymentHistory
  }))

  const formatCurrency = (amount) => {
    const value = Number(amount) || 0
    try {
      return value.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })
    } catch (_) {
      return `₹${value.toFixed(2)}`
    }
  }

  async function fetchPaymentHistory() {
    if (!salesRegisterId) {
      setPaymentHistory([])
      return
    }
    try {
      const res = await axios.get(`${getApiBaseUrl()}/salesregister/getPaymentReceiptHistoryById`, {
        params: { UserId: userId, SalesRegisterId: salesRegisterId }
      })
      if (res.status === 200) {
        const payload = res?.data?.data
        const normalized = Array.isArray(payload) ? payload : Array.isArray(payload?.[0]) ? payload[0] : []
        setPaymentHistory(normalized)
      }
    } catch (err) {
      console.log(err)
      CreateAlert('Problem loading payment history', 'Error', setAlert)
    }
  }

  const startEditReceipt = (row) => {
    const receiptId = row?.SalesRegisterPaymentReceiptId || row?.SalesRegisterPaymentReceiptID || row?.salesRegisterPaymentReceiptId
    if (!receiptId) {
      CreateAlert('Receipt id not found. Please deploy latest DB SP `sp_GetSalesRegisterPaymentReceiptHistoryBySalesRegisterId` and refresh.', 'Warning', setAlert)
      return
    }
    const dt = row?.PaymentDate ? new Date(row.PaymentDate) : new Date()
    setEditingReceiptId(receiptId)
    setEditForm({
      ReceiptDate: isNaN(dt.getTime()) ? new Date() : dt,
      Amount: row?.Amount !== undefined && row?.Amount !== null ? String(row.Amount) : '',
      Remarks: row?.Remarks || ''
    })
  }

  const cancelEditReceipt = () => {
    setEditingReceiptId(null)
    setEditForm({ ReceiptDate: new Date(), Amount: '', Remarks: '' })
  }

  const saveEditedReceipt = async () => {
    if (!editingReceiptId) return
    const raw = String(editForm?.Amount ?? '').replace(/,/g, '').trim()
    const amount = Number(raw)
    if (!raw || !Number.isFinite(amount) || amount <= 0) {
      CreateAlert('Please enter a valid receipt amount', 'Warning', setAlert)
      return
    }

    setSavingReceiptId(editingReceiptId)
    try {
      const payload = {
        UserId: userId,
        SalesRegisterPaymentReceiptId: editingReceiptId,
        ReceiptDate: moment(editForm.ReceiptDate).format('YYYY-MM-DD HH:mm:ss'),
        Amount: amount,
        Remarks: String(editForm?.Remarks ?? '').trim() || null
      }
      const res = await updateSalesPaymentReceipt(payload)
      const ok = res?.success ?? true
      if (!ok) {
        CreateAlert(res?.message || 'Unable to update receipt', 'Warning', setAlert)
        return
      }
      CreateAlert(res?.message || 'Receipt updated', 'Success', setAlert)
      cancelEditReceipt()
      await fetchPaymentHistory()
      onReceiptChanged?.()
    } catch (err) {
      console.log(err)
      CreateAlert(err?.response?.data?.message || 'Unable to update receipt', 'Error', setAlert)
    } finally {
      setSavingReceiptId(null)
    }
  }

  const deleteReceipt = async (row) => {
    const receiptId = row?.SalesRegisterPaymentReceiptId || row?.SalesRegisterPaymentReceiptID || row?.salesRegisterPaymentReceiptId
    if (!receiptId) {
      CreateAlert('Receipt id not found. Please deploy latest DB SP `sp_GetSalesRegisterPaymentReceiptHistoryBySalesRegisterId` and refresh.', 'Warning', setAlert)
      return
    }

    setSavingReceiptId(receiptId)
    try {
      const res = await deleteSalesPaymentReceipt({ UserId: userId, SalesRegisterPaymentReceiptId: receiptId })
      const ok = res?.success ?? true
      if (!ok) {
        CreateAlert(res?.message || 'Unable to delete receipt', 'Warning', setAlert)
        return
      }
      CreateAlert(res?.message || 'Receipt deleted', 'Success', setAlert)
      if (editingReceiptId === receiptId) cancelEditReceipt()
      await fetchPaymentHistory()
      onReceiptChanged?.()
    } catch (err) {
      console.log(err)
      CreateAlert(err?.response?.data?.message || 'Unable to delete receipt', 'Error', setAlert)
    } finally {
      setSavingReceiptId(null)
    }
  }

  async function fetchFollowups() {
    if (!salesRegisterId) {
      setFollowupList([])
      return
    }
    try {
      const res = await axios.get(`${getApiBaseUrl()}/salesregister/getPaymentFollowupById`, {
        params: { UserId: userId, SalesRegisterId: salesRegisterId }
      })
      if (res.status === 200) {
        const payload = res?.data?.data
        const normalized = Array.isArray(payload) ? payload : Array.isArray(payload?.[0]) ? payload[0] : []
        setFollowupList(normalized)
      }
    } catch (err) {
      console.log(err)
      CreateAlert('Problem loading follow-up', 'Error', setAlert)
    }
  }

  const startEditFollowup = (followup) => {
    setEditingFollowupId(followup?.SalesRegisterPaymentFollowupId || null)
    setEditingFollowupText(String(followup?.FollowupRemarks || ''))
    setEditingFollowupDate(followup?.FollowupDate ? new Date(followup.FollowupDate) : new Date())
  }

  const cancelEditFollowup = () => {
    setEditingFollowupId(null)
    setEditingFollowupText('')
    setEditingFollowupDate(null)
  }

  const saveEditedFollowup = async () => {
    if (!editingFollowupId) return
    if (!editingFollowupDate || isNaN(editingFollowupDate.getTime())) {
      CreateAlert('Please select a valid follow-up date', 'Warning', setAlert)
      return
    }
    if (!String(editingFollowupText || '').trim()) {
      CreateAlert('Please enter follow-up remarks', 'Warning', setAlert)
      return
    }

    setFollowupSaving(true)
    try {
      const payload = {
        UserId: currentUserId,
        SalesRegisterPaymentFollowupId: editingFollowupId,
        FollowupDate: moment(editingFollowupDate).format('YYYY-MM-DD HH:mm:ss'),
        FollowupRemarks: String(editingFollowupText || '').trim()
      }
      const res = await updateSalesPaymentFollowup(payload)
      const ok = res?.success ?? true
      if (!ok) {
        CreateAlert(res?.message || 'Unable to update follow-up', 'Warning', setAlert)
        return
      }
      CreateAlert(res?.message || 'Follow-up updated', 'Success', setAlert)
      cancelEditFollowup()
      await fetchFollowups()
    } catch (err) {
      console.log(err)
      CreateAlert(err?.response?.data?.message || 'Unable to update follow-up', 'Error', setAlert)
    } finally {
      setFollowupSaving(false)
    }
  }

  const openDeleteFollowupConfirm = (followup) => {
    setDeleteFollowupConfirm({
      show: true,
      followupId: followup?.SalesRegisterPaymentFollowupId || null,
      followupText: String(followup?.FollowupRemarks || ''),
      followupUser: followup?.FullName || '',
      followupDate: followup?.FollowupDate || ''
    })
  }

  const closeDeleteFollowupConfirm = () => {
    setDeleteFollowupConfirm({
      show: false,
      followupId: null,
      followupText: '',
      followupUser: '',
      followupDate: ''
    })
  }

  const deleteFollowup = async (followupId) => {
    if (!followupId) return
    setFollowupSaving(true)
    try {
      const res = await deleteSalesPaymentFollowup({ UserId: currentUserId, SalesRegisterPaymentFollowupId: followupId })
      const ok = res?.success ?? true
      if (!ok) {
        CreateAlert(res?.message || 'Unable to delete follow-up', 'Warning', setAlert)
        return
      }
      CreateAlert(res?.message || 'Follow-up deleted', 'Success', setAlert)
      if (editingFollowupId === followupId) cancelEditFollowup()
      await fetchFollowups()
      closeDeleteFollowupConfirm()
    } catch (err) {
      console.log(err)
      CreateAlert(err?.response?.data?.message || 'Unable to delete follow-up', 'Error', setAlert)
    } finally {
      setFollowupSaving(false)
    }
  }

  const confirmDeleteFollowup = () => {
    if (deleteFollowupConfirm?.followupId) deleteFollowup(deleteFollowupConfirm.followupId)
  }

  async function saveFollowup() {
    if (!salesRegisterId) {
      CreateAlert('Missing sales reference', 'Error', setAlert)
      return
    }
    if (!remarks.trim()) {
      CreateAlert('Please add remarks', 'Warning', setAlert)
      return
    }

    try {
      const payload = {
        UserId: userId,
        SalesRegisterId: salesRegisterId,
        FollowupDate: moment(followupDate).format('YYYY-MM-DD HH:mm:ss'),
        FollowupRemarks: remarks
      }

      const res = await axios.post(`${getApiBaseUrl()}/salesregister/savePaymentFollowup`, payload)
      if (res.status === 200) {
        setRemarks('')
        fetchFollowups()
        CreateAlert('Follow-up saved!', 'Success', setAlert)
      }
    } catch (err) {
      console.log(err)
      CreateAlert(err?.response?.data?.message || 'Problem saving follow-up', 'Error', setAlert)
    }
  }

  useEffect(() => {
    fetchPaymentHistory()
    fetchFollowups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesRegisterId])

  return (
    <>
      <SKAlerts alert={alert} />
      <Modal show={deleteFollowupConfirm.show} onHide={closeDeleteFollowupConfirm} centered>
        <ModalHeader closeButton>
          <ModalTitle>Delete Follow-up</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="text-muted small mb-2">Are you sure you want to delete this follow-up?</div>
          <ul className="mb-0">
            <li><b>User :</b> {deleteFollowupConfirm.followupUser || '-'}</li>
            <li><b>Date :</b> {deleteFollowupConfirm.followupDate ? moment(deleteFollowupConfirm.followupDate).format('DD/MM/YYYY, h:mm A') : '-'}</li>
            <li><b>Remark :</b> {deleteFollowupConfirm.followupText || '-'}</li>
          </ul>
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-outline-secondary btn-sm" onClick={closeDeleteFollowupConfirm} disabled={followupSaving}>
            Cancel
          </button>
          <button className="btn btn-danger btn-sm" onClick={confirmDeleteFollowup} disabled={followupSaving}>
            {followupSaving ? 'Deleting…' : 'Delete'}
          </button>
        </ModalFooter>
      </Modal>
      <div className="row" style={{ margin: '10px' }}>
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Payment History</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: '200px' }}>Type</th>
                      <th style={{ width: '180px' }}>Date</th>
                      <th style={{ width: '180px', textAlign: 'right' }}>Amount</th>
                      <th>Remarks</th>
                      <th style={{ width: '120px', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory?.map((p, i) => {
                      const isReceipt = String(p?.PaymentTypeLabel || '').toLowerCase() === 'receipt'
                      const receiptId = p?.SalesRegisterPaymentReceiptId || p?.SalesRegisterPaymentReceiptID || p?.salesRegisterPaymentReceiptId
                      const isEditing = isReceipt && receiptId && receiptId === editingReceiptId
                      const isBusy = Boolean(receiptId && savingReceiptId === receiptId)

                      return (
                        <tr key={`${p?.PaymentTypeLabel || 'payment'}-${p?.PaymentDate || i}-${receiptId || i}`}>
                          <td>{p?.PaymentTypeLabel || '-'}</td>
                          <td>
                            {isEditing ? (
                              <DatePicker
                                className="form-control form-control-sm"
                                selected={editForm.ReceiptDate}
                                onChange={(date) => setEditForm((prev) => ({ ...prev, ReceiptDate: date }))}
                              />
                            ) : (
                              p?.PaymentDate ? moment(p.PaymentDate).format('MM/DD/YYYY') : '-'
                            )}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {isEditing ? (
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={editForm.Amount}
                                onChange={(e) => setEditForm((prev) => ({ ...prev, Amount: e.target.value }))}
                                style={{ textAlign: 'right' }}
                                placeholder="0.00"
                              />
                            ) : (
                              formatCurrency(p?.Amount)
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={editForm.Remarks}
                                onChange={(e) => setEditForm((prev) => ({ ...prev, Remarks: e.target.value }))}
                                placeholder="Remark"
                              />
                            ) : (
                              p?.Remarks || '-'
                            )}
                          </td>
                          <td>
                            {!isReceipt ? (
                              <span className="text-muted small">-</span>
                            ) : isEditing ? (
                              <div className="edit-delete-action">
                                <Link
                                  className="me-2 p-2"
                                  style={{ display: 'inline-flex', cursor: isBusy ? 'not-allowed' : 'pointer', opacity: isBusy ? 0.6 : 1 }}
                                  title="Save"
                                  onClick={() => { if (!isBusy) saveEditedReceipt() }}
                                >
                                  <Icons.Save size={14} data-feather="save" className="feather-save" />
                                </Link>
                                <Link
                                  className="p-2"
                                  style={{ display: 'inline-flex', cursor: isBusy ? 'not-allowed' : 'pointer', opacity: isBusy ? 0.6 : 1 }}
                                  title="Cancel"
                                  onClick={() => { if (!isBusy) cancelEditReceipt() }}
                                >
                                  <Icons.X size={14} data-feather="x" className="feather-x" />
                                </Link>
                              </div>
                            ) : (
                              <div className="edit-delete-action">
                                <Link
                                  className="me-2 p-2"
                                  style={{ display: 'inline-flex', cursor: isBusy ? 'not-allowed' : 'pointer', opacity: isBusy ? 0.6 : 1 }}
                                  title="Edit"
                                  onClick={() => { if (!isBusy) startEditReceipt(p) }}
                                >
                                  <Icons.Edit data-feather="edit" className="feather-edit" />
                                </Link>
                                <Link
                                  className="confirm-text p-2"
                                  style={{ display: 'inline-flex', cursor: isBusy ? 'not-allowed' : 'pointer', opacity: isBusy ? 0.6 : 1 }}
                                  title="Delete"
                                  onClick={() => { if (!isBusy) deleteReceipt(p) }}
                                >
                                  <Icons.Trash2 data-feather="trash-2" className="feather-trash-2" />
                                </Link>
                              </div>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                    {(!paymentHistory || paymentHistory.length === 0) && (
                      <tr>
                        <td colSpan={5} className="text-muted small">
                          No payments available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-3 align-self-start">
          <label className="form-label">Next Follow-up Date</label>
          <DatePicker
            className="form-control"
            selected={followupDate}
            onChange={(date) => setFollowupDate(date)}
            minDate={new Date()}
            dateFormat="dd/MM/yyyy"
          />
        </div>

        <div className="col-lg-9 align-self-start">
          <label className="form-label">Remarks</label>
          <textarea
            className="form-control"
            rows={1}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Add follow-up details..."
            style={{
              height: 'auto',
              minHeight: '4.5rem',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
          <div className="mt-2">
            <button className="btn btn-primary btn-sm me-2" onClick={saveFollowup}>
              Save
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => setRemarks('')}>
              Cancel
            </button>
          </div>
        </div>

        <div className="col-12">
          <div className="scrollable-comments">
            {followupList?.map((f, i) => (
              <div className="card card-bg-light mg-em" key={f?.SalesRegisterPaymentFollowupId || i}>
                <div
                  className="card-body position-relative"
                  style={{ margin: '5px' }}
                  onMouseEnter={() => setHoveredFollowupId(f?.SalesRegisterPaymentFollowupId)}
                  onMouseLeave={() => setHoveredFollowupId(null)}
                >
                  {Number(f?.CreatedBy) === currentUserId && editingFollowupId !== f?.SalesRegisterPaymentFollowupId && (
                    <>
                      {hoveredFollowupId === f?.SalesRegisterPaymentFollowupId && (
                        <div className="d-flex align-items-center gap-2" style={{ position: 'absolute', top: '10px', right: '10px' }}>
                          <span
                            role="button"
                            title="Edit"
                            className="text-info"
                            style={{ cursor: followupSaving ? 'not-allowed' : 'pointer', opacity: followupSaving ? 0.6 : 1 }}
                            onClick={() => { if (!followupSaving) startEditFollowup(f) }}
                          >
                            <Icons.Edit size={16} className="feather-edit" />
                          </span>
                          <span
                            role="button"
                            title="Delete"
                            className="text-danger"
                            style={{ cursor: followupSaving ? 'not-allowed' : 'pointer', opacity: followupSaving ? 0.6 : 1 }}
                            onClick={() => { if (!followupSaving) openDeleteFollowupConfirm(f) }}
                          >
                            <Icons.Trash2 size={16} className="feather-trash-2" />
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  <div className="d-flex">
                    <div className="me-3">
                      <span className="avatar avatar-rounded">
                        <img alt="" src={`/assets/img/profiles/${f?.UserImagePath || 'avatar-03.jpg'}`} />
                      </span>
                    </div>
                    <div className="flex-grow-1">
                      <div className="fw-semibold">{f?.FullName || '-'}</div>

                      {editingFollowupId === f?.SalesRegisterPaymentFollowupId ? (
                        <>
                          <div className="row g-2 align-items-center">
                            <div className="col-sm-4">
                              <DatePicker
                                className="form-control form-control-sm"
                                selected={editingFollowupDate}
                                onChange={(date) => setEditingFollowupDate(date)}
                              />
                            </div>
                            <div className="col-sm-8">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={editingFollowupText}
                                onChange={(e) => setEditingFollowupText(e.target.value)}
                                placeholder="Remark"
                              />
                            </div>
                          </div>
                          <div className="mt-2 d-flex gap-2">
                            <button className="btn btn-primary btn-sm" onClick={saveEditedFollowup} disabled={followupSaving}>
                              {followupSaving ? 'Saving…' : 'Save'}
                            </button>
                            <button className="btn btn-outline-secondary btn-sm" onClick={cancelEditFollowup} disabled={followupSaving}>
                              Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        <div dangerouslySetInnerHTML={{ __html: f?.FollowupRemarks || '' }}></div>
                      )}

                      <small className="text-muted">
                        {f?.CreatedDate ? moment(f.CreatedDate).format('DD/MM/YYYY, h:mm A') : ''}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {(!followupList || followupList.length === 0) && (
              <div className="text-muted small">No follow-ups available.</div>
            )}
          </div>
        </div>
      </div>
    </>
  )
})

export default PaymentFollowupRowFollowup

